import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

/**
 * Route guard.
 *
 * MUST wait on auth.loading. The access token is memory-only, so immediately
 * after a reload `user` is legitimately null while the silent refresh is in
 * flight — redirecting then would bounce a logged-in user on every refresh.
 *
 * Convenience only. Spec 6.1: "Guard the route server-side too." The API
 * re-checks every request regardless of what the client renders.
 *
 * Lives in its own file so routes.tsx can be imported by the prerenderer
 * without dragging in the browser router.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="route-loading" aria-busy="true" />;

  /**
   * INTERIM TARGET. The prototype's requireAuth() (index.html:751) does not
   * navigate — it opens the auth modal and replays the action after login.
   * Home ("/") is not built, so /courses is the real destination. `from` is
   * carried so the modal replay can be wired when Home lands in Phase 9.
   */
  if (!user) return <Navigate to="/courses" replace state={{ from: location.pathname, authRequired: true }} />;
  return <>{children}</>;
}
